import { useEffect, useState, useCallback, useMemo } from "react";
import "../styles.css";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import ApiScenariosManagement from "../../../../api/scenarios-management";
import { Body } from "../../../../components/main-body/body";
import PageContentLoader from "../../../../components/loader/page-content-loader";
import UploadCard from "./upload-card";
import UploadModal from "./upload-modal";
import DropdownFilters from "../../../../components/dropdown-filters/dropdown-filters";
import debounce from "../../../../common/utils/debounce";
import moment from "moment";

const Uploads = () => {
  const [searchValue, setSearchValue] = useState("");
  const { id } = useParams();
  const [groupedUploads, setGroupedUploads] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filters, setFilters] = useState({});
  const { t } = useTranslation();
  const [sourceList, setSourceList] = useState([]);

  const formatWeekRange = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const now = moment();

    const isCurrentWeek = now.isSame(start, 'week');

    const sameMonth = start.month() === end.month() && start.year() === end.year();
    const sameYear = start.year() === end.year();

    let range;

    if (sameMonth && sameYear) {
      range = `${start.format("D")} – ${end.format("D MMM")}`;
    } else if (sameYear) {
      range = `${start.format("D MMM")} – ${end.format("D MMM")}`;
    } else {
      range = `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
    }

    return isCurrentWeek ? `Current week (${range})` : range;
  };

  const groupUploadsByWeek = (uploads) => {
    const result = {};

    uploads.forEach((upload) => {
      const date = moment(upload.createdAt);
      const startOfWeek = date.clone().startOf("week"); // Sunday
      const endOfWeek = date.clone().endOf("week");     // Saturday

      const weekKey = formatWeekRange(startOfWeek, endOfWeek);

      if (!result[weekKey]) {
        result[weekKey] = [];
      }

      result[weekKey].push(upload);
    });

    return result;
  };

  const fetchSourceList = () => {
    ApiScenariosManagement.getSources().then((r) => {
      setSourceList(
        r.data.sources.map((s) => ({
          id: s.id,
          name: s.title,
        }))
      );
    });
  };

  const fetchImages = async (currentPage = 1, query = searchValue, currentFilters = filters) => {
    try {
      const response = await ApiSubjectsManagement.getSubjectUploads(id, {
        ...currentFilters,
        query,
        page: currentPage,
        createdAtEnd: currentFilters.period?.[currentFilters.period.length - 1]?.end,
        createdAtStart: currentFilters.period?.[currentFilters.period.length - 1]?.start,
      });

      const newUploads = response.data.uploads;
      const newGrouped = groupUploadsByWeek(newUploads);

      setGroupedUploads((prev) => {
        const merged = { ...prev };

        for (const [weekKey, uploads] of Object.entries(newGrouped)) {
          if (!merged[weekKey]) {
            merged[weekKey] = uploads;
          } else {
            const existingIds = new Set(merged[weekKey].map((u) => u.id));
            const uniqueNew = uploads.filter((u) => !existingIds.has(u.id));
            merged[weekKey] = [...merged[weekKey], ...uniqueNew];
          }
        }

        return merged;
      });

      setHasMore(newUploads.length > 0);
      if (newUploads.length > 0) {
        setPage((prev) => prev + 1);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setHasMore(false);
    }
  };

  useEffect(() => {
    if (page > 1) {
      fetchImages(page);
    }
  }, [page]);

  const debouncedGetUploads = useCallback(debounce(fetchImages, 400), []);

  const handleUpdateSearchValue = useCallback((value) => {
    setSearchValue(value);
    setPage(1);
    setGroupedUploads([]);
    debouncedGetUploads(1, value, filters);
  }, [filters, debouncedGetUploads]);

  const isFilterEmpty = (filter) => {
    return Object.keys(filter).length === 0 ||
      Object.values(filter).every(value =>
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && value !== null && Object.keys(value).length === 0) ||
        value === undefined || value === null
      );
  };

  const handleUpdateFilters = useCallback((newFilters) => {
    let equal_filters = null
    setFilters((prevFilters) => {
      let empty_filters = isFilterEmpty(prevFilters) && isFilterEmpty(newFilters)
      equal_filters = JSON.stringify(prevFilters) !== JSON.stringify(newFilters) && empty_filters
      if (JSON.stringify(prevFilters) === JSON.stringify(newFilters)) {
        return prevFilters;
      }
      return newFilters;
    });
    if (!equal_filters) {
      setPage(1);
      setGroupedUploads([]);
      debouncedGetUploads(1, searchValue, newFilters);
    }

  }, [searchValue, debouncedGetUploads]);

  useEffect(() => {
    console.log("useEffect fetchImages");
    fetchSourceList();
    fetchImages(1);
  }, []);

  const headerProps = useMemo(
    () => ({
      title: "Uploads",
      search: {
        value: searchValue,
        onHandleChange: handleUpdateSearchValue,
        placeholder: t("assets.btn.search"),
      },
    }),
    [searchValue, handleUpdateSearchValue]
  );

  return (
    <Body
      header={headerProps}
      scroll
      filters={
        <DropdownFilters
          filtersBy={[
            { name: "Source", id: "sources", children: sourceList },
            { name: "Date period", id: "period", period: true },
          ]}
          updateFilterValue={handleUpdateFilters}
        />
      }
    >
      <InfiniteScroll
        dataLength={Object.values(groupedUploads).flat().length}
        next={() => fetchImages(page)}
        hasMore={hasMore}
        loader={<PageContentLoader />}
      >
        {Object.entries(groupedUploads).map(([week, uploads]) => (
          <div key={week}>
            <div className="week-period">{week}</div>
            <div className="upload-grid">
              {uploads.map((image) => (
                <UploadCard key={image.id} image={image} onClick={() => setSelectedImage(image)} />
              ))}
            </div>
          </div>
        ))}
      </InfiniteScroll>
      {selectedImage && (
        <UploadModal
          image={selectedImage}
          isOpen={Boolean(selectedImage)}
          close={() => setSelectedImage(null)}
        />
      )}
    </Body>
  );
};

export default Uploads;
